/* eslint-disable no-nested-ternary */
/* eslint-disable no-unused-vars */
import { useEffect, useState } from 'react';
import { ScaleLoader } from 'react-spinners';
import { useNavigate } from 'react-router-dom';
import Button from '@/ui/_lib/Button';
// import { useToast } from '@/ui/_lib/Toast/useToast';
import { ReactComponent as TextIconLogo } from '@/assets/image/text-icon-logo.svg';
import { useUser } from '@/contexts/UserContext/UserContext';
import { CheckUserTitleService, UserService } from '@/ui/_apiUrls';
import { cn, isValidSlug } from '@/utils';

export default function ProfileTitle() {
  // const { toast } = useToast();
  const { user, setUser } = useUser();
  const navigate = useNavigate();
  const [isMounted, setIsMounted] = useState(false);
  const [title, setTitle] = useState('');
  const [debouncedTitle, setDebouncedTitle] = useState<string | null>(null);
  const [titleError, setTitleError] = useState(false);

  const safeUser = user || ({} as any);

  // Move all hooks before any conditional returns
  const { mutate: patchUser } = UserService.patch({
    mutationParams: {
      onSuccess: (response) => {
        // Use the title from the response if available, otherwise use the local state
        const titleFromResponse = response?.result?.title || title;
        const updatedUser = {
          ...safeUser,
          ...response.result,
          title: titleFromResponse,
          profileComplete: response.result.profileComplete || safeUser.profileComplete
        };
        setUser(updatedUser);
        navigate('/complete-profile');
      },
      onError: () => {

      }
    },
  });

  const {
    data: titleCheckData,
    isError: isTitleCheckError,
    isLoading: isCheckingTitle,
    error: titleCheckError,
  } = CheckUserTitleService.get({
    queryParams: {
      queryKey: ['check_title_status', debouncedTitle],
      enabled: !!debouncedTitle?.length && isValidSlug(debouncedTitle) && title.length >= 3,
    },
    params: { title: debouncedTitle },
  });

  // Extract error message from the API response
  const getErrorMessage = () => {
    if (titleError) {
      if (title.length < 3) return 'Title must be at least 3 characters long';
      if (!isValidSlug(title)) return 'Title can only contain letters, numbers, underscores, and hyphens';
      return 'Enter a valid title.';
    }
    
    if (titleCheckError) {
      // Extract error message from the API response
      const errorMessage = titleCheckError?.message;
      if (errorMessage) {
        return errorMessage;
      }
      return 'Cannot check the title. Something went wrong on our side.';
    }
    
    if (titleCheckData?.status === 'not_available') {
      return titleCheckData?.message || 'Title not available';
    }
    
    return '';
  };

  const errorMessage = getErrorMessage();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTitle(title);
      setTitleError(() => {
        if (!title.length) return false;
        if (title.length < 3) return true;
        if (!isValidSlug(title)) return true;
        return false;
      });
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [title]);

  if (!isMounted) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <ScaleLoader />
      </div>
    );
  }

  return (
    <div className=" flex w-full flex-col items-center justify-center gap-10">
      <TextIconLogo />
      <div>
        <p className="text-center font-roboto text-[1.5rem] font-semibold text-[#475467]">
          Hi, {!safeUser?.name?.toLowerCase().includes('unnamed') ? safeUser?.name : safeUser?.email} . 👋🏻
        </p>
        <p className="text-center text-[#475467]">Enter your profile title to get started!</p>
      </div>
      <div className="form-control flex w-full flex-col gap-2">
        <span className="flex w-full items-center justify-between">
          <p>
            Profile Title<span className="text-red-600">&nbsp;*</span>
          </p>
        </span>
        <input
          type="text"
          name="title"
          placeholder="Profile Title"
          className={cn(
            'max-w-full overflow-x-auto rounded-lg border  bg-[#F8F8FF] p-3 text-base md:text-sm placeholder-gray-400 outline-none',
            titleError || isTitleCheckError || titleCheckData?.status === 'not_available'
              ? 'border-red-400'
              : titleCheckData?.status === 'available'
                ? 'border-green-600'
                : 'border-gray-300',
          )}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value.toLowerCase());
          }}
        />
        <div className="flex w-full items-center justify-between">
          {errorMessage && <p className="text-xs text-[#ec5050]">{errorMessage}</p>}
          {!errorMessage && titleCheckData && (
            <p
              className={cn(
                'text-xs text-[#475467]',
                titleCheckData?.status === 'not_available'
                  ? 'text-red-400'
                  : titleCheckData?.status === 'available'
                    ? 'text-green-600'
                    : 'text-gray-300',
              )}
            >
              {titleCheckData?.message}
            </p>
          )}
          {isCheckingTitle && <ScaleLoader height={10} color="#36d7b7" />}
        </div>
        <span className=" flex flex-col flex-wrap gap-1 text-xs text-[#475467]">
          <p>Your Linkozi page will be accessed from : </p>
          <a href={`${import.meta.env.VITE_SITE_URL}/${title || safeUser.title || '<title>'}`} className=" flex flex-wrap text-[#039BE5]">
            {import.meta.env.VITE_SITE_URL}/{title || safeUser.title || '<title>'}
          </a>
        </span>
        {isTitleCheckError && (
          <div className="rounded-md bg-red-300 p-2 text-white">
            Cannot check the title. Somethig went wrong on our side.
          </div>
        )}
      </div>

      {/* Search bar design here */}

      <div className="  flex w-full justify-center">
        <Button
          style={
            titleError || isTitleCheckError || !title || isCheckingTitle || titleCheckData?.status === 'not_available'
              ? { boxShadow: 'none' }
              : {}
          }
          variant={
            titleError || isTitleCheckError || !title || isCheckingTitle || titleCheckData?.status === 'not_available'
              ? 'ghost'
              : 'primary'
          }
          className="w-1/2 lg:w-24"
          onClick={() => {
            patchUser({ title });
          }}
        >
          Next
        </Button>
      </div>
    </div>
  );
}